import { RawInputData, TransformedTableData } from "types/tableConversion.types"
import { createEmptyTransformedData } from "utils/tableConversion/tableConversionHelpers"
import { detectDataFormat } from "utils/tableConversion/dataFormatDetector"
import { convertDTABToChart } from "utils/tableConversion/converters/dtabConverter"
import { convertXTABSimpleToChart } from "utils/tableConversion/converters/xtabSimpleConverter"
import { convertXTABMultiToChart } from "utils/tableConversion/converters/xtabMultiConverter"
import { SIG_STAT, STATISTIC_LABEL } from "utils/tableModel"

// Re-export types for backward compatibility
export type {
  BarChartDataItem,
  PieChartSlice,
  PieChartStatisticGroup,
  TransformedTableData,
} from "types/tableConversion.types"

type GridCell = string | number | null

/** A letters line holds no numbers in its data area (letters or blanks only). */
const hasNoNumbers = (values: (GridCell | undefined)[]): boolean =>
  values.every((value) => typeof value !== "number")

/**
 * Removes the significance letters (`sig` statistic) from an engine grid, so
 * charts never see letters. Anchored on the "Statistic" cell at (sx, sy):
 * - stats down: a row below it whose label in column sx is `sig`;
 * - stats across (`OUTPUT /T`): a column right of it whose label in row sy
 *   is `sig`;
 * and in both cases only when the line's data cells hold no numbers. A
 * header category that happens to be called "sig" always carries numbers,
 * so it is kept. The other statistic labels are not inspected, so an
 * unknown statistic never stops the stripping.
 *
 * A grid without letters lines is returned as the same array.
 */
export const stripSigRows = (
  data: (string | number | null)[][]
): (string | number | null)[][] => {
  if (!Array.isArray(data)) return data

  let sy = -1
  let sx = -1
  for (let y = 0; y < data.length && sy < 0; y++) {
    const row = data[y]
    if (!Array.isArray(row)) continue
    const x = row.indexOf(STATISTIC_LABEL)
    if (x >= 0) {
      sy = y
      sx = x
    }
  }
  if (sy < 0) return data

  // Stats down: letters rows.
  const dropRows = new Set<number>()
  for (let y = sy + 1; y < data.length; y++) {
    const row = data[y]
    if (
      Array.isArray(row) &&
      row[sx] === SIG_STAT &&
      hasNoNumbers(row.slice(sx + 1))
    ) {
      dropRows.add(y)
    }
  }
  if (dropRows.size > 0) return data.filter((_, y) => !dropRows.has(y))

  // Stats across (/T): letters columns.
  const statRow = data[sy]
  const dropColumns = new Set<number>()
  for (let x = sx + 1; x < statRow.length; x++) {
    if (
      statRow[x] === SIG_STAT &&
      hasNoNumbers(data.slice(sy + 1).map((row) => row?.[x]))
    ) {
      dropColumns.add(x)
    }
  }
  if (dropColumns.size > 0) {
    return data.map((row) =>
      Array.isArray(row) ? row.filter((_, x) => !dropColumns.has(x)) : row
    )
  }

  return data
}

/**
 * Enhanced convertDataToTable function that handles DTAB, XTAB_SIMPLE, and XTAB_MULTI formats
 * Now includes special handling for null category edge cases.
 * Significance letters (`sig` rows / columns) are stripped first (see `stripSigRows`).
 */
export const convertDataToTable = (
  data: RawInputData | null | undefined
): TransformedTableData => {
  if (!data || !Array.isArray(data.data) || data.data.length === 0) {
    return createEmptyTransformedData()
  }

  const grid = stripSigRows(data.data)
  const input: RawInputData = grid === data.data ? data : { ...data, data: grid }

  const dataFormat = detectDataFormat(input)

  switch (dataFormat) {
    case "DTAB":
      return convertDTABToChart(input)
    case "XTAB_SIMPLE":
      return convertXTABSimpleToChart(input)
    case "XTAB_MULTI":
    default:
      return convertXTABMultiToChart(input)
  }
}
